import React, { useEffect, useState } from "react";
import "./PageList.css";
import * as constants from "../../../../constants/pageTypes";
import { Table, HeaderCell, Cell, Column } from "rsuite-table";
import { usePageList } from "../../../../../contexts/PageListProvider/PageListProvider";
import { canUsePage } from "../../../../../utils/pageRestrictions";
import ILessonPage from "../../../../../models/ILessonPage";

const isPageDisabled = (rowData: ILessonPage) => !canUsePage(rowData as Parameters<typeof canUsePage>[0]);

const getThumbnailClass = (pagePlayerType: string) => {
  switch (pagePlayerType) {
    case constants.ThreeD_PAGE:
      return "thumbnail thumbnail-container-ThreeDModel";
    case constants.QUIZ_PAGE:
      return "thumbnail thumbnail-container-Quiz";
    case constants.BASIC_PAGE:
      return "thumbnail thumbnail-container-Base";
    case constants.FMS_PAGE:
      return "thumbnail thumbnail-container-FMS";
    case constants.WALK_AROUND:
      return "thumbnail thumbnail-container-Walkaround";
    case constants.CABIN:
      return "thumbnail thumbnail-container-Cabin";
    default:
      return "";
  }
};

interface ImageCellProps {
  rowData?: ILessonPage;
  activeRow: number;
  [key: string]: unknown;
}

const ImageCell = ({ rowData, activeRow, ...rest }: ImageCellProps) => {
  if (!rowData) return null;

  const thumbnailClass = getThumbnailClass(rowData.pagePlayerType);
  const disabled = isPageDisabled(rowData);
  const isSelected = rowData.pageVersionId === activeRow;
  const rowClassName = isSelected ? "lesson-box selected-full" : "lesson-box";

  return (
    <Cell {...rest}>
      <div
        className={rowClassName}
        style={{ opacity: disabled ? 0.4 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
      >
        <div className={"pageBox-container"}>
          <div className={thumbnailClass}></div>
          <div style={{ marginLeft: "10px" }}>{rowData.order + 1 + ". " + rowData.name}</div>
        </div>
      </div>
    </Cell>
  );
};

export interface PageListProps {
  PopulateList: boolean;
  LessonVersionId: number;
  SetPageVersionId: (id: number) => void;
  SetSelectedPage: (page: ILessonPage | null) => void;
}

const PageList = (props: PageListProps) => {
  const { PopulateList, LessonVersionId, SetPageVersionId, SetSelectedPage } = props;
  const [activeRow, setActiveRow] = useState<number>(0);
  const [lessonPageList, pageListStateDispatch] = usePageList(); // PageListProvider for retrieving the lesson's page list

  useEffect(() => {
    if (PopulateList && LessonVersionId !== 0) {
      pageListStateDispatch({
        type: "GET_PAGES",
        lessonVersionId: LessonVersionId,
      });
    } else {
      resetList();
    }
  }, [LessonVersionId]);

  useEffect(() => {
    if (lessonPageList?.pageList) {
      for (const page in lessonPageList.pageList) {
        if (lessonPageList.pageList[page].pageVersionId === activeRow) {
          SetSelectedPage(lessonPageList.pageList[page]);

          return;
        }
      }
    }
  }, [activeRow]);

  const resetList = () => {
    pageListStateDispatch({ type: "NO_DATA" });
    setActiveRow(0);
  };

  const handleOnRowClick = (rowData: ILessonPage) => {
    if (isPageDisabled(rowData)) return;
    setActiveRow(rowData.pageVersionId);
    SetPageVersionId(rowData.pageVersionId);
  };

  const renderLoading = () => {
    return (
      <div className="spin-container">
        <div className="spin" id="loader"></div>
        <div className="spin" id="loader2"></div>
        <div className="spin" id="loader3"></div>
        <div className="spin" id="loader4"></div>
        <span id="text">LOADING...</span>
      </div>
    );
  };

  return (
    <>
      <Table
        fillHeight
        data={lessonPageList?.pageList}
        className="page-list-table"
        bordered
        hover
        loading={lessonPageList.loading}
        renderLoading={renderLoading}
        onMouseDown={(e) => e.stopPropagation()}
        rowHeight={127}
        shouldUpdateScroll={false}
        onRowClick={handleOnRowClick}
      >
        <Column flexGrow={1}>
          <HeaderCell>Page Name and Number in Lesson Designer</HeaderCell>
          <ImageCell dataKey="name" activeRow={activeRow} />
        </Column>
      </Table>
    </>
  );
};

export default PageList;
